import { SerializedStyles, css } from "@emotion/react";
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  endOfToday,
  format,
  isAfter,
  isBefore,
  isSameDay,
  parse,
  startOfDay,
  startOfToday,
  subYears,
} from "date-fns";
import { isEqual } from "lodash";
import memoizeOne from "memoize-one";
import * as React from "react";
import { Descendant, Element, Text } from "slate";

import { DesignToken, RadiusToken, SpacingToken } from "@livechat/design-system-react-components";

import { config } from "config/config";
import { DateFormat } from "constants/date";
import {
  AgentAssignmentFilterEnum,
  AttachmentsFilterEnum,
  Filter,
  GroupFilterEnum,
  IDateRangeWithCompareFilter,
  IFilters,
  NotTaggedFilterId,
  RatingFilter,
} from "constants/filters";
import { DELIVERY_ISSUES } from "constants/filters/delivery-status-filter";
import { TicketsRoutes, routeTitles } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction, batchActionStatusMap } from "constants/tickets/batch-action";
import { Column, ColumnWidths } from "constants/tickets/columns";
import { escapeString } from "helpers/string";
import {
  Author,
  DetailedSource,
  EventType,
  IEvent,
  IEventAuthor,
  ITicket,
  ITicketSource,
  Rating,
  TicketSilo,
  TicketSourceReferenceType,
  TicketSpamReason,
  TicketStatus,
  TicketStatusFormattedValues,
} from "interfaces/entities/ticket";
import { SettingsRoutes } from "routes/settings/constants";
import { IAgent } from "store/entities/agents";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { ITicketsViewBatchUpdatePayloadData } from "store/views/tickets/interfaces";
import { withVar } from "themes/helpers";
import { normalizeBrowserLink } from "utils/string";

import { timeAgo } from "./date";
import { url as urlRegExp } from "./regexp";

export const getColumnWidthsCss = (column: Column): SerializedStyles => css`
  ${ColumnWidths[column] ? `width: ${ColumnWidths[column]}px` : null};
`;

export const messageEventsList = [EventType.Message, EventType.Rating];

export function eventSupportsMessage(event: IEvent): boolean {
  return messageEventsList.includes(event.type);
}

export function getLastMessageEvent(events: IEvent[]): IEvent | null {
  if (events.length === 0) {
    return null;
  }
  return [...events].reverse().find((e: IEvent) => eventSupportsMessage(e)) || null;
}

export function getLastPublicMessageEvent(events: IEvent[]): IEvent | null {
  if (events.length === 0) {
    return null;
  }
  return [...events].reverse().find((e: IEvent) => e.type === EventType.Message && !e.isPrivate) || null;
}

export function getLastCustomerMessageEvent(events: IEvent[]): IEvent | null {
  if (events.length === 0) {
    return null;
  }
  return (
    [...events].reverse().find((e: IEvent) => e.type === EventType.Message && e.author?.type === Author.Customer) ||
    null
  );
}

export function getToastContent(
  ticketIds: string[],
  batchAction: TicketsBatchAction,
  data?: ITicketsViewBatchUpdatePayloadData
): string {
  if (batchAction === TicketsBatchAction.ChangeAssignee) {
    if (ticketIds.length === 1) {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      return `Ticket has been assigned to ${data.assignee.name}.`;
    }
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return `${ticketIds.length} tickets have been assigned to ${data.assignee.name}.`;
  }

  if (batchAction === TicketsBatchAction.ChangeTeam) {
    if (ticketIds.length === 1) {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      return `Ticket has been moved to ${data.team.name}.`;
    }
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return `${ticketIds.length} tickets have been moved to ${data.team.name}.`;
  }

  const status = batchActionStatusMap[batchAction];
  if (ticketIds.length === 1) {
    return `Ticket has been marked as ${TicketStatusFormattedValues[status]}.`;
  }
  return `${ticketIds.length} tickets have been marked as ${TicketStatusFormattedValues[status]}.`;
}

export interface IFiltersValuesDependencies {
  currentAgentId: string;
}

const defaultFilterValuesForRoute: {
  [key in TicketsRoutes]: (dependencies: IFiltersValuesDependencies) => Partial<IFilters>;
} = {
  [TicketsRoutes.All]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
  }),
  [TicketsRoutes.Search]: () => ({ [Filter.Silo]: TicketSilo.Tickets }),
  [TicketsRoutes.Unassigned]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.Open],
    [Filter.AgentAssignment]: [AgentAssignmentFilterEnum.Unassigned],
    [Filter.Group]: [GroupFilterEnum.MyTeams],
  }),
  [TicketsRoutes.MyOpen]: ({ currentAgentId }) => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.Open],
    [Filter.Agent]: [currentAgentId],
  }),
  [TicketsRoutes.Open]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.Open],
  }),
  [TicketsRoutes.Pending]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.Pending],
  }),
  [TicketsRoutes.OnHold]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.OnHold],
  }),
  [TicketsRoutes.Solved]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.Solved],
  }),
  [TicketsRoutes.Closed]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.TicketStatus]: [TicketStatus.Closed],
  }),
  [TicketsRoutes.Archive]: () => ({
    [Filter.Silo]: TicketSilo.Archive,
  }),
  [TicketsRoutes.Spam]: () => ({
    [Filter.Silo]: TicketSilo.Spam,
  }),
  [TicketsRoutes.Trash]: () => ({
    [Filter.Silo]: TicketSilo.Trash,
  }),
  [TicketsRoutes.Undelivered]: () => ({
    [Filter.Silo]: TicketSilo.Tickets,
    [Filter.DeliveryStatus]: DELIVERY_ISSUES,
    [Filter.DeliveryUpdatedAtFrom]: "-7D",
  }),
};
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getFilterValuesForRoute(route: TicketsRoutes, dependencies: IFiltersValuesDependencies) {
  return defaultFilterValuesForRoute[route](dependencies);
}

export const filtersToOmitInURLForRoute: { [key in TicketsRoutes]: Filter[] } = {
  [TicketsRoutes.All]: [Filter.Silo],
  [TicketsRoutes.Search]: [Filter.Silo],
  [TicketsRoutes.Unassigned]: [Filter.Silo, Filter.TicketStatus, Filter.Agent, Filter.AgentAssignment, Filter.Group],
  [TicketsRoutes.MyOpen]: [Filter.Silo, Filter.TicketStatus, Filter.Agent, Filter.AgentAssignment, Filter.Group],
  [TicketsRoutes.Open]: [Filter.Silo, Filter.TicketStatus],
  [TicketsRoutes.Pending]: [Filter.Silo, Filter.TicketStatus],
  [TicketsRoutes.OnHold]: [Filter.Silo, Filter.TicketStatus],
  [TicketsRoutes.Solved]: [Filter.Silo, Filter.TicketStatus],
  [TicketsRoutes.Closed]: [Filter.Silo, Filter.TicketStatus],
  [TicketsRoutes.Archive]: [...Object.values(Filter)],
  [TicketsRoutes.Spam]: [...Object.values(Filter)],
  [TicketsRoutes.Trash]: [...Object.values(Filter)],
  [TicketsRoutes.Undelivered]: [...Object.values(Filter)],
};

const eventsValidInSummary = [EventType.Message, EventType.Rating, EventType.RatingRequestSent];

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function eventIsValidInSummary(event: IEvent) {
  return eventsValidInSummary.includes(event.type);
}

export function getTicketSummary(ticket: ITicket): { firstMessage?: IEvent; lastMessage?: IEvent } {
  const firstMessage = ticket.events.find(eventIsValidInSummary);
  const lastMessage = [...ticket.events].reverse().find(eventIsValidInSummary);

  if (!firstMessage) {
    return {};
  }
  return { firstMessage, lastMessage };
}

/**
 * @deprecated remove when convenient
 */
export function routeSupportsBatchUpdate(_route: TicketsRoutes, _filters: IFilters, _currentPage: number): boolean {
  return true;
}

export function getRouteByPathname(pathname: string): TicketsRoutes | null {
  if (!pathname) {
    return null;
  }

  const splitPath = pathname.split("/").filter(Boolean).pop();

  if (!splitPath) {
    return null;
  }

  const isInRoutes = function isInRoutes(path: string | TicketsRoutes): path is TicketsRoutes {
    return Object.values(TicketsRoutes).includes(path as TicketsRoutes);
  };

  const path = splitPath.split("?")[0].split("#")[0];
  if (isInRoutes(path)) {
    return path;
  }

  return null;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const prepareRichNodeText = (text: string) =>
  text
    .replace(/\n\r/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[&<"']/g, char => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case '"':
          return "&quot;";
        default:
          return "&#039;";
      }
    })
    .replace(/\n/g, "<br />");

function parseRichTextElementHead(element: Element): { startTag: string; endTag: string } {
  switch (element.type) {
    case "list-item":
      return { startTag: "<li>", endTag: "</li>" };
    case "bulleted-list":
      return { startTag: "<ul>", endTag: "</ul>" };
    case "numbered-list":
      return { startTag: "<ol>", endTag: "</ol>" };
    case "paragraph":
      return { startTag: "<p>", endTag: "</p>" };
    case "heading-one":
      return { startTag: "<h1>", endTag: "</h1>" };
    case "heading-two":
      return { startTag: "<h2>", endTag: "</h2>" };
    case "heading-three":
      return { startTag: "<h3>", endTag: "</h3>" };
    case "heading-four":
      return { startTag: "<h4>", endTag: "</h4>" };
    case "heading-five":
      return { startTag: "<h5>", endTag: "</h5>" };
    case "heading-six":
      return { startTag: "<h6>", endTag: "</h6>" };
    case "block-quote":
      return { startTag: "<blockquote>", endTag: "</blockquote>" };
    case "link": {
      const { success, url } = normalizeBrowserLink(element.url);

      return success
        ? {
            startTag: `<a href="${url?.trim()}" target="_blank" rel="nofollow noreferrer noopener">`,
            endTag: "</a>",
          }
        : { startTag: "<a>", endTag: "</a>" };
    }
    case "image":
      return {
        // @ts-expect-error TS2345
        startTag: `<img src="${element.url}" class="hd-image" alt="${escapeString(element.alt) || "image"}" width="${
          element.width
        }" height="${element.height}" />`,
        endTag: "",
      };
    case "mention":
      return {
        startTag: `<span class="mention">@${prepareRichNodeText(element.agentName)}`,
        endTag: "</span>",
      };
    case "htmlSnippet":
      return {
        startTag: element.content,
        endTag: "",
      };
    case "textVariable":
      return {
        startTag: `<span style="height: 20px; padding: 1px ${withVar(SpacingToken.Spacing2)};border-color: ${withVar(
          DesignToken.BorderBasicPrimary
        )}; border-radius: ${withVar(RadiusToken.Radius3)}; background-color: ${withVar(
          DesignToken.SurfaceAccentEmphasisLowPurple
        )}; color: ${withVar(DesignToken.ContentBasicPurple)}; margin-right: ${withVar(
          SpacingToken.Spacing1
        )}; margin-left: ${withVar(SpacingToken.Spacing1)};">${element.label}`,
        endTag: "</span>",
      };
    default:
      return { startTag: "", endTag: "" };
  }
}

function parseRichTextNode(node: Text): string {
  let text = prepareRichNodeText(node.text);

  if (text === "") {
    return "";
  }

  if (node.bold) {
    text = `<strong>${text}</strong>`;
  }

  if (node.underline) {
    text = `<span style='text-decoration: underline'>${text}</span>`;
  }

  if (node.code) {
    text = `<code>${text}</code>`;
  }

  if (node.italic) {
    text = `<span style='font-style: italic'>${text}</span>`;
  }

  if (node.strikethrough) {
    text = `<span style='text-decoration: line-through'>${text}</span>`;
  }

  return text;
}

export function parseRichTextFormatToHtml(message: Descendant[]): string {
  let resultHTML = "";

  message.forEach(node => {
    if (Element.isElement(node)) {
      const { startTag, endTag } = parseRichTextElementHead(node);
      const text = parseRichTextFormatToHtml(node.children);
      resultHTML += startTag;
      resultHTML += node.type === "paragraph" && text === "" ? "<br />" : text;
      resultHTML += endTag;
    } else if (Text.isText(node)) {
      resultHTML += parseRichTextNode(node);
    }
  });

  return resultHTML;
}

export function parseEventMessageToHtml(message = ""): string {
  let msg = message;
  msg = msg.trim();
  msg = msg.replace(/<BR>/g, "\n");
  msg = escapeString(msg);
  msg = msg.replace(/\n{3,}/g, "\n\n");

  msg = msg
    .split(urlRegExp)
    .filter(Boolean)
    .map((splitted: string) => {
      const isUrl = urlRegExp.test(splitted);
      if (isUrl) {
        const href = splitted.startsWith("www.") ? `http://${splitted}` : splitted;
        return `<a target="_blank" rel="nofollow noreferrer noopener" href="${href}">${splitted}</a>`;
      }
      return splitted;
    })
    .join("");

  msg = msg.replace(/\n/g, "<BR>");

  return msg;
}

type DetailedSourceLabels =
  | "Created manually"
  | "ChatBot"
  | "Contact form"
  | "Email"
  | "Chat"
  | "WordPress"
  | "API"
  | "Contact widget"
  | "Webflow"
  | "AI agent"
  | "Copilot"
  | "Workflows";

export const ticketDetailedSourcesLabel: Record<DetailedSource, DetailedSourceLabels> = {
  [DetailedSource.CreatedManually]: "Created manually",
  [DetailedSource.Email]: "Email",
  [DetailedSource.ContactForm]: "Contact form",
  [DetailedSource.Api]: "API",
  [DetailedSource.Chat]: "Chat",
  [DetailedSource.ContactWidget]: "Contact widget",
  [DetailedSource.Webflow]: "Webflow",
  [DetailedSource.ChatBot]: "ChatBot",
  [DetailedSource.WordPress]: "WordPress",
  [DetailedSource.AiAgent]: "AI agent",
  [DetailedSource.Copilot]: "Copilot",
  [DetailedSource.Workflows]: "Workflows",
};

const getLiveChatArchivedChatLink = (threadId: string): string => `${config.livechatAgentAppHost}/archives/${threadId}`;

export interface IDetailedSourceLink {
  href: string;
  external: boolean;
}

export function getTicketDetailedSourceLink(
  source: ITicketSource,
  contactForms: Record<string, IContactForm>
): IDetailedSourceLink | null {
  const { detailedSource, referenceUrl, referenceId, contactFormId, referenceType, isBooster } = source;

  if (detailedSource === DetailedSource.Chat) {
    if (referenceId) {
      return { href: getLiveChatArchivedChatLink(referenceId), external: true };
    }
    if (referenceUrl) {
      return { href: referenceUrl, external: true };
    }
  }

  if (
    (detailedSource === DetailedSource.AiAgent ||
      detailedSource === DetailedSource.Copilot ||
      detailedSource === DetailedSource.Workflows) &&
    referenceId
  ) {
    return { href: getLiveChatArchivedChatLink(referenceId), external: true };
  }

  if (detailedSource === DetailedSource.ChatBot && referenceId && referenceType) {
    if (referenceType === TicketSourceReferenceType.Lc2) {
      return { href: getLiveChatArchivedChatLink(referenceId), external: true };
    }
    if (referenceType === TicketSourceReferenceType.ChatBot) {
      return { href: `${config.chatBotAppHost}/v2/archives/${referenceId}`, external: true };
    }
  }

  if (detailedSource === DetailedSource.WordPress && contactFormId) {
    return {
      href: `${SettingsRoutes.ContactForms}/edit/${contactFormId}`,
      external: false,
    };
  }

  if (detailedSource === DetailedSource.ContactForm && contactFormId) {
    const isLiveChatWelcomeScreenContactForm = Boolean(isBooster);
    const contactFormExists = Boolean(contactForms[contactFormId]);

    return {
      href: contactFormExists
        ? `${isLiveChatWelcomeScreenContactForm ? SettingsRoutes.WidgetForms : SettingsRoutes.ContactForms}/edit/${contactFormId}`
        : SettingsRoutes.ContactForms,
      external: false,
    };
  }

  return null;
}

export const getSpamReasonMessage = (reason: TicketSpamReason): string => {
  switch (reason) {
    case TicketSpamReason.HeaderAutogenerated:
      return "The requester's email address seems to be auto-generated (mailing list, autoresponders, etc.).";
    case TicketSpamReason.HeaderSpam:
      return "The message content has a high spam score (incorrect headlines, low-quality content, etc.)";
    case TicketSpamReason.LicenseBlackList:
      return "The requester's email address is on the Blocked list.";
    case TicketSpamReason.LimitPerHour:
      return "The requester exceeded the limit of messages sent per hour.";
    case TicketSpamReason.SimilarEmail:
      return "A very similar message content was seen earlier.";
    case TicketSpamReason.TooManyRecipients:
      return "Too many recipients were added to the message.";
    case TicketSpamReason.MalwareDomains:
      return "A link used in the message content is suspicious.";
    case TicketSpamReason.MalwareAttachments:
      return "The attachment added to the message is suspicious.";
    default:
      return "";
  }
};

export function getLiveChatTicketId(ticket: ITicket): string | null {
  if (ticket.integrations && ticket.integrations.livechat && ticket.integrations.livechat.externalId) {
    return ticket.integrations.livechat.externalId;
  }
  return null;
}

export function getEventDate(eventDate: Date): string {
  return format(eventDate, DateFormat.DateTime);
}

export function getFormattedEventDate(eventDate: Date): string {
  const now = new Date();
  if (isSameDay(eventDate, now)) {
    return timeAgo(eventDate, now, { dateFormat: DateFormat.DateWithoutDayOfTheWeek, useStrict: false });
  }
  return getEventDate(eventDate);
}

export function isShortId(id: string): boolean {
  return id.length === 6;
}

const getDefaultColumns = (isMobile: boolean, _route: TicketsRoutes = TicketsRoutes.All): Column[] => {
  return isMobile
    ? [Column.Mobile, Column.MobileOptions]
    : [Column.Requester, Column.Subject, Column.Assignee, Column.Status, Column.LastMessageDate, Column.Options];
};

export const getColumns = memoizeOne(getDefaultColumns);

export function getIsMemberOfTicketTeam(agent: IAgent, ticket: ITicket): boolean {
  return agent.teamIds.includes(ticket.assignment.team.id);
}

export function getRouteTitle(route: TicketsRoutes): string {
  return routeTitles[route];
}

export function messageIsEventDescription(message: React.ReactElement | string): boolean {
  return typeof message === "string";
}

export function getPlainTextMessageText(message: React.ReactElement): string {
  return message?.props?.children || "";
}

export function messageIsPlainText(message: React.ReactElement): boolean {
  return message.type === React.Fragment;
}

export function messageIsEmpty(message: React.ReactElement | string, trim = true): boolean {
  const messageContent = typeof message === "string" ? message : getPlainTextMessageText(message);
  if (typeof messageContent !== "string") {
    return false;
  }

  const element = document.createElement("div");
  element.innerHTML = messageContent;
  if (trim) {
    // @ts-expect-error TS(2304): Cannot find name 'element'.
    element.textContent = element.textContent.trim();
  }
  // @ts-expect-error TS18047
  return element.textContent.length === 0 && !messageContent.includes("<img");
}

const humanAuthorsList = [Author.Agent, Author.Customer];

export function authorIsHuman(author: Author): boolean {
  return humanAuthorsList.includes(author);
}

export function eventSupportsDeliveryStatus(type: EventType, author: IEventAuthor | null): boolean {
  return (
    author?.type === Author.Agent ||
    author?.type === Author.System ||
    author?.type === Author.Rule ||
    type === EventType.RatingRequestSent
  );
}

const eventsWithTicketFeedMessage = [EventType.RatingRequestSent, EventType.Rating];
export function eventContainsTicketFeedMessage(type: EventType): boolean {
  return eventsWithTicketFeedMessage.includes(type);
}

export function doesMessageIncludeCustomText(message: string, signature: string): boolean {
  const trimmedMessage = message.trim();
  const trimmedSignature = signature ? signature.trim() : "";

  if (trimmedSignature.length === 0) {
    return trimmedMessage.length > 0;
  }

  return trimmedMessage.length > 0 && trimmedMessage !== trimmedSignature;
}

export function trimAgentSignature(message: string, signature: string | null): string {
  const signatureWithWhitespace = `\n${signature || ""}`;
  const signatureLength = signatureWithWhitespace.length;
  if (message.substring(message.length - signatureLength) === signatureWithWhitespace) {
    return message.substring(0, message.length - signatureLength);
  }
  return message;
}

function isTicketAssignmentStatusSatisfied(ticket: ITicket, value: AgentAssignmentFilterEnum[]): boolean {
  return value.some(status => {
    switch (status) {
      case AgentAssignmentFilterEnum.Assigned: {
        return ticket.assignment.agent;
      }
      case AgentAssignmentFilterEnum.Unassigned: {
        return !ticket.assignment.agent;
      }
      default:
        return false;
    }
  });
}

const parseRelativeRangeEdge = (edge: string): Date => {
  const [value, unit] = [parseInt(edge.slice(0, -1), 10), edge.slice(-1)];
  const relative = startOfToday();

  if (!Number.isSafeInteger(value)) {
    return relative;
  }

  switch (unit) {
    case "D":
      return addDays(relative, value);
    case "W":
      return addWeeks(relative, value);
    case "M":
      return addMonths(relative, value);
    case "Y":
      return addYears(relative, value);
    default:
      return relative;
  }
};

export function isTargetInDateRangeFilter(
  target: Date | undefined,
  filter: IDateRangeWithCompareFilter | undefined
): boolean {
  if (!target || !filter) return false;

  let fromDate = null;
  let toDate = null;

  if (filter.from === null || filter.from === undefined) {
    fromDate = subYears(startOfToday(), -100);
  } else if (typeof filter.from === "string" && !filter.from.endsWith("Z")) {
    const { from } = filter;
    fromDate = parseRelativeRangeEdge(from);
  } else {
    fromDate = filter.from ? startOfDay(parse(filter.from)) : startOfToday();
  }

  if (filter.to === null || filter.to === undefined) {
    toDate = addDays(endOfToday(), 7);
  } else if (typeof filter.to === "string" && !filter.to.endsWith("Z")) {
    const { to } = filter;
    toDate = parseRelativeRangeEdge(to);
  } else {
    toDate = filter.to ? endOfDay(parse(filter.to)) : endOfToday();
  }

  return (
    (isAfter(target, fromDate) || isEqual(target, fromDate)) && (isBefore(target, toDate) || isEqual(target, toDate))
  );
}

function isTicketHavingProperRating(ticket: ITicket, ratingFilters: RatingFilter[]): boolean {
  return ratingFilters.some(filter => {
    switch (filter) {
      case RatingFilter.NotRated:
        return !ticket.rating;
      case RatingFilter.Rated:
        return ticket.rating;
      case RatingFilter.RatedGood:
        return ticket.rating === Rating.Good;
      case RatingFilter.RatedBad:
        return ticket.rating === Rating.Bad;
      case RatingFilter.RatedNeutral:
        return ticket.rating === Rating.Neutral;
      default:
        return false;
    }
  });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const parseAgentFilters = (filters: IFilters[Filter.Agent], loggedInAgentId: string) =>
  // @ts-expect-error TS18048
  filters.map(agentFilter => (agentFilter === "ME" ? loggedInAgentId : agentFilter));

function isTicketSatisfyingFilter(
  filterType: Filter,
  value: IFilters[keyof IFilters],
  ticket: ITicket,
  agent: IAgent
): boolean {
  if (value === null) {
    return true;
  }

  switch (filterType) {
    case Filter.Agent: {
      const agentIds = parseAgentFilters(value as IFilters[Filter.Agent], agent.id);
      // @ts-expect-error TS2345
      return agentIds.includes(ticket.assignment.agent?.id);
    }
    case Filter.AgentAssignment: {
      const payload = value as IFilters[Filter.AgentAssignment];
      // @ts-expect-error TS2345
      return isTicketAssignmentStatusSatisfied(ticket, payload);
    }

    case Filter.LastActivityDateRange: {
      const payload = value as IFilters[Filter.LastActivityDateRange];
      const target = ticket.updatedAt;

      return isTargetInDateRangeFilter(target, payload);
    }

    case Filter.LastMessageDateRange: {
      const payload = value as IFilters[Filter.LastMessageDateRange];
      const target = ticket.lastMessageAt;

      return isTargetInDateRangeFilter(target, payload);
    }

    case Filter.CreationDateRange: {
      const payload = value as IFilters[Filter.CreationDateRange];
      const target = ticket.date;

      return isTargetInDateRangeFilter(target, payload);
    }
    case Filter.Tag: {
      const payload = value as IFilters[Filter.Tag];
      // @ts-expect-error TS18048
      const notTagged = payload.includes(NotTaggedFilterId);

      // @ts-expect-error TS18048
      return notTagged ? ticket.tagIds.length === 0 : ticket.tagIds.some(tagId => payload.includes(tagId));
    }
    case Filter.Rating: {
      const payload = value as IFilters[Filter.Rating];
      // @ts-expect-error TS2345
      return isTicketHavingProperRating(ticket, payload);
    }
    case Filter.TicketStatus: {
      const payload = value as IFilters[Filter.TicketStatus];
      // @ts-expect-error TS18048
      return payload.some(status => status === ticket.status);
    }
    case Filter.Silo: {
      const silo = value as IFilters[Filter.Silo];
      return silo === ticket.silo;
    }
    case Filter.DetailedSource: {
      const payload = value as IFilters[Filter.DetailedSource];
      // @ts-expect-error TS18048
      return payload.some(source => source === ticket.source?.detailedSource);
    }
    case Filter.Group: {
      const payload = value as IFilters[Filter.Group];
      // @ts-expect-error TS18048
      const inMyTeams = payload.includes("MY_TEAMS")
        ? agent.teamIds.some(teamID => ticket.teamIds.includes(teamID))
        : true;
      const inTicketTeams =
        // @ts-expect-error TS18048
        payload.filter(t => t !== "MY_TEAMS").length > 0
          ? // @ts-expect-error TS18048
            payload.filter(t => t !== "MY_TEAMS").some(teamID => ticket.teamIds.includes(teamID))
          : true;

      return inTicketTeams && inMyTeams;
    }
    case Filter.Priority: {
      const payload = value as IFilters[Filter.Priority];
      // @ts-expect-error TS18048
      switch (payload.priorityOp || "eq") {
        case "eq":
          // @ts-expect-error TS18048
          return payload.priority.includes(ticket.priority);
        case "gt":
          // @ts-expect-error TS18048
          return payload.priority.every(priority => ticket.priority > priority);
        case "gte":
          // @ts-expect-error TS18048
          return payload.priority.every(priority => ticket.priority >= priority);
        case "lt":
          // @ts-expect-error TS(2339): Property 'priority' does not exist on type 'JSX.In... Remove this comment to see the full error message
          return payload.priority.every(priority => ticket.priority < priority);
        case "lte":
          // @ts-expect-error TS18048
          return payload.priority.every(priority => ticket.priority <= priority);
        default:
          return true;
      }
    }
    case Filter.Followers: {
      const payload = value as IFilters[Filter.Followers];
      // @ts-expect-error TS18048
      return payload.every(followerID => {
        if (followerID === "ME") {
          return ticket.followers.includes(agent.id);
        }
        return ticket.followers.includes(followerID);
      });
    }
    case Filter.Attachments: {
      const payload = value as IFilters[Filter.Attachments];

      // @ts-expect-error TS18048
      return payload.every(attachmentClaim => {
        switch (attachmentClaim) {
          case AttachmentsFilterEnum.Yes:
            return ticket.events.some(t => (t.attachments || []).length > 0);
          case AttachmentsFilterEnum.No:
            return ticket.events.every(t => (t.attachments || []).length === 0);
          default:
            return true;
        }
      });
    }
    case Filter.DeliveryStatus: {
      const payload = value as IFilters[Filter.DeliveryStatus];
      return (payload || []).some(status => status === ticket.deliveryStatus);
    }

    default:
      return true;
  }
}

export function isTicketSatisfyingFilters(
  filters: Partial<IFilters>,
  ticket: ITicket,
  agent: IAgent,
  silo?: TicketSilo,
  route?: TicketsRoutes
): boolean {
  const appliedFilters = route === TicketsRoutes.Search ? { ...filters, silo } : filters;
  return Object.entries(appliedFilters).every(([type, value]) =>
    isTicketSatisfyingFilter(type as Filter, value, ticket, agent)
  );
}
